import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import img1 from "../assets/images/HomePage/hospice-4135835_1920.jpg";
import img2 from "../assets/images/HomePage/hospital-1802680_1920.jpg";
import img3 from "../assets/images/HomePage/hospital-ward-1338585_1920.jpg";
import img4 from "../assets/images/HomePage/skin-2404156_1920.jpg";
import img5 from "../assets/images/HomePage/skin-2404166_1920.jpg";

const sliderImages = [
  { src: img1, alt: "Hospice" },
  // { src: img2, alt: "Hospital" },
  // { src: img3, alt: "Hospital Ward" },
  // { src: img4, alt: "Skin 1" },
  // { src: img5, alt: "Skin 2" },
];

const features = [
  {
    title: "Automated Rounds",
    description:
      "Save time and reduce errors with digital, trackable room rounds.",
    icon: "🕒",
  },
  {
    title: "Real-Time Insights",
    description: "Instant dashboards for compliance, maintenance, and more.",
    icon: "📊",
  },
  {
    title: "Compliance Tracking",
    description:
      "Effortlessly meet regulatory requirements and generate reports.",
    icon: "✅",
  },
  {
    title: "Maintenance Module",
    description: "Log, assign, and resolve issues from any device.",
    icon: "🛠️",
  },
  {
    title: "Mobile Friendly",
    description: "Access RoomRounds from any device, anywhere, anytime.",
    icon: "📱",
  },
  {
    title: "Secure & HIPAA Compliant",
    description: "Your data is protected with industry-leading security.",
    icon: "🔒",
  },
  {
    title: "Customizable Workflows",
    description: "Adapt RoomRounds to your facility's unique needs.",
    icon: "⚙️",
  },
  {
    title: "24/7 Support",
    description: "Our team is always here to help you succeed.",
    icon: "💬",
  },
];

const testimonials = [
  {
    quote:
      "RoomRounds transformed our compliance process. We save hours every week!",
    name: "Sarah L.",
    title: "Director of Nursing, CareWell SNF",
  },
  {
    quote:
      "The real-time dashboards give us peace of mind and help us stay survey-ready.",
    name: "James P.",
    title: "Administrator, GreenFields Facility",
  },
  {
    quote: "The mobile app is a game changer for our staff on the go.",
    name: "Priya S.",
    title: "Charge Nurse, Oakwood SNF",
  },
];

const fadeIn = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, type: "spring" as const },
  },
};

const buttonTap = { scale: 0.95 };
const buttonHover = { scale: 1.05 };

// New content sections
const industryProblems = [
  {
    title: "Rising Operational Costs",
    bullets: [
      "Nearly half of skilled nursing facilities run at a negative margin — on razor-thin, often sub-1% margins, there's no room for operational waste.",
      "Manual room rounds and maintenance coordination consume 5–30 hours of leadership time per week — pulled straight from resident care.",
      "Paper-based workflows let tasks slip through the cracks, quietly adding cost in a business that can't absorb it.",
    ],
  },
  {
    title: "Regulatory Compliance Pressure",
    bullets: [
      "Constant, unannounced surveys make perpetual readiness the only safe posture — and evolving CMS requirements keep the bar moving.",
      "Costly deficiencies: facilities average 9.5 per survey cycle, and 27% receive at least one serious citation — with penalties that can reach tens of thousands of dollars.",
      "No real-time visibility into compliance tasks turns every survey into a scramble to reconstruct evidence from paper.",
    ],
  },
  {
    title: "Labor Shortages and Staff Overload",
    bullets: [
      "Chronic staffing shortages stretch administrators and teams thin, pulling focus away from residents.",
      "Manual tracking of rounds and maintenance piles administrative work onto an already-overloaded team.",
      "With high turnover at every level, institutional knowledge walks out the door — and new staff start blind.",
    ],
  },
  {
    title: "Operational Tools Without the HIPAA Overhead",
    bullets: [
      "Most facility software is clinical (EHR) — powerful, but heavy, costly, and slow to deploy under full HIPAA scope.",
      "Administrators need a simple, non-clinical tool to run operations without months of IT and legal review.",
      "Until now, almost nothing automated the non-clinical work — rounds, maintenance, escalation — that fills an administrator's day.",
    ],
  },
];

const rrSolutions = [
  {
    title: "Streamlined Operations to Reduce Costs",
    bullets: [
      "Automates rounds and maintenance coordination — giving leadership back 5–30 hours a week for residents instead of paperwork.",
      "Real-time dashboards put the whole building's status on one screen. No more chasing updates through meetings and group texts.",
      "Priced from $250/month with $0 setup — fits thin SNF budgets and pays for itself fast.",
    ],
  },
  {
    title: "Enhanced Compliance Management",
    bullets: [
      "Every round, task, and issue is timestamped and documented automatically — so survey evidence is retrieved in seconds, not reconstructed from paper.",
      "Real-time issue escalation surfaces compliance risks the moment they appear, not at the next meeting.",
      "Turns survey readiness from a fire drill into a byproduct of the daily work — protecting the Five-Star rating that protects your margin.",
    ],
  },
  {
    title: "Empowering Staff with Easy-to-Use Tools",
    bullets: [
      "Mobile-first (iOS/Android) so staff manage tasks from anywhere in the building — no clipboard, no back-to-the-station trips.",
      "Intuitive enough to learn in minutes — critical when turnover means you're always onboarding someone new.",
      "Assigns and tracks tasks in real time, freeing staff to spend their time with residents instead of on paperwork.",
    ],
  },
  {
    title: "Simplified Deployment, No HIPAA Overhead",
    bullets: [
      "Stays out of PHI scope — so you skip the months-long IT and legal review that slows clinical software, and deploy in days.",
      "Purpose-built for the non-clinical operational work — rounds, maintenance, escalation — that no other platform covers.",
      "Maintenance module schedules and tracks year-round preventive and reactive tasks, keeping the building survey-ready.",
    ],
  },
];

const Home: React.FC = () => {
  // Slider state
  const [current, setCurrent] = useState(0);
  const nextSlide = () =>
    setCurrent((prev) => (prev + 1) % sliderImages.length);
  const prevSlide = () =>
    setCurrent(
      (prev) => (prev - 1 + sliderImages.length) % sliderImages.length
    );

  // Auto-slide
  // React.useEffect(() => {
  //   const timer = setInterval(nextSlide, 4000);
  //   return () => clearInterval(timer);
  // }, []);

  return (
    <div className="bg-gradient-to-br from-blue-50 to-white min-h-screen w-full flex flex-col">
      {/* Full Screen Slider Section */}
      <section className="relative w-full h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <motion.img
          key={sliderImages[current].src}
          src={sliderImages[current].src}
          alt={sliderImages[current].alt}
          className="absolute inset-0 w-full h-full object-cover object-center transition-all duration-700 z-0"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={fadeIn}
        />
        <div className="absolute inset-0 bg-blue-900/40 z-10" />
        <div className="relative z-20 flex flex-col items-center justify-center w-full h-full text-white text-center px-4">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4 drop-shadow-lg">
            RoomRounds
          </h1>
          <p className="mb-6 text-lg md:text-2xl max-w-2xl mx-auto drop-shadow">
            {/* Modernize your hospital or SNF with automated rounds, real-time
            compliance, and effortless maintenance tracking - all designed to
            complement your existing EHR. Built for healthcare leaders who
            demand excellence in service and safety. */}
            The operational layer for skilled nursing. Automated rounds, real-time compliance, and effortless maintenance tracking, designed to complement your existing EHR. Built for the leaders who demand excellence in service and safety.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-8 w-full justify-center">
            <motion.div
              whileTap={buttonTap}
              whileHover={buttonHover}
              className="w-full sm:w-auto"
            >
              <Link
                to="/free-trial"
                className="bg-blue-600 text-white px-8 py-3 rounded-lg shadow hover:bg-blue-700 transition w-full sm:w-auto block"
              >
                Start Free Trial
              </Link>
            </motion.div>
            <motion.div
              whileTap={buttonTap}
              whileHover={buttonHover}
              className="w-full sm:w-auto"
            >
              <Link
                to="/contact?source=schedule-demo"
                className="bg-white/90 border border-blue-600 text-blue-700 px-8 py-3 rounded-lg shadow hover:bg-blue-50 transition w-full sm:w-auto block"
              >
                Schedule Demo
              </Link>
            </motion.div>
          </div>
        </div>
        {/* <button
          aria-label="Previous slide"
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-blue-700 rounded-full p-2 shadow-md z-30"
        >
          &#8592;
        </button>
        <button
          aria-label="Next slide"
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-blue-700 rounded-full p-2 shadow-md z-30"
        >
          &#8594;
        </button> */}
        {/* <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2 z-30">
          {sliderImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`w-3 h-3 rounded-full border-2 border-white ${current === idx ? "bg-blue-600" : "bg-blue-200"}`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div> */}
      </section>

      {/* Features Section */}
      {/* <section className="max-w-7xl mx-auto py-12 px-4 w-full">
        <h2 className="text-3xl font-bold mb-8 text-center text-blue-700">Why Choose RoomRounds?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              className="bg-white rounded-lg shadow p-6 flex flex-col items-center hover:shadow-xl transition min-h-[180px]"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeIn}
            >
              <div className="text-4xl mb-3">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2 text-blue-800">{feature.title}</h3>
              <p className="text-gray-600 text-sm text-center">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section> */}

      {/* Testimonials Section */}
      {/* <section className="bg-blue-50 py-12 w-full">
        <h2 className="text-2xl font-bold text-center text-blue-700 mb-8">What Our Users Say</h2>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 px-4">
          {testimonials.map((t) => (
            <div key={t.name} className="bg-white rounded-lg shadow p-6 flex flex-col items-center">
              <div className="text-blue-600 text-3xl mb-2">“</div>
              <p className="text-gray-700 italic mb-4">{t.quote}</p>
              <div className="font-semibold text-blue-800">{t.name}</div>
              <div className="text-gray-500 text-sm">{t.title}</div>
            </div>
          ))}
        </div>
      </section> */}

      {/* Industry Problems Section */}
      <section className="max-w-7xl mx-auto py-12 px-4 w-full">
        <h2 className="text-3xl font-bold mb-8 text-center text-blue-700">
          Industry Problems Faced by Skilled Nursing Facilities (SNFs)
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
          {industryProblems.map((p) => (
            <motion.div
              key={p.title}
              className="bg-white rounded-lg shadow p-6 hover:shadow-xl transition"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeIn}
            >
              <h3 className="text-xl font-bold mb-3 text-blue-800">
                {p.title}
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-lg text-gray-700">
                {p.bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Solutions Section */}
      <section className="bg-blue-50 py-12 w-full">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center text-blue-700">
            How RoomRounds Solves These Problems
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
            {rrSolutions.map((s) => (
              <motion.div
                key={s.title}
                className="bg-white rounded-lg shadow p-6 hover:shadow-xl transition"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeIn}
              >
                <h3 className="text-xl font-bold mb-3 text-blue-800">
                  {s.title}
                </h3>
                <ul className="list-disc pl-5 space-y-2 text-lg text-gray-700">
                  {s.bullets.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-12 text-center w-full">
        <h2 className="text-3xl font-bold mb-8 text-center text-blue-800">
          Ready to transform your facility?
        </h2>
        <p className="mb-6 text-lg text-gray-700">
          Give your team back their time — and your building its memory.
        </p>
        <Link
          to="/free-trial"
          className="bg-blue-600 text-white px-8 py-3 rounded-lg shadow hover:bg-blue-700 transition"
        >
          Start Your Free Trial
        </Link>
      </section>
    </div>
  );
};

export default Home;
